import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import clsx from "clsx";

import AppBox from "@/components/AppBox";
import AppIcon from "@/components/AppIcon";
// import AppTooltip from "@/components/AppTooltip";
import AppText from "@/components/AppText";
import { initialPagesList, adminPagesList } from "@/constants/pagesList";

interface AppSidebarProps {
  onCollapseChange: (isCollapsed: boolean) => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({onCollapseChange}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { me } = useAppSelector((state: any) => state.auth);

  const [pagesList, setPagesList] = useState(initialPagesList);
  const [menuOpen, setMenuOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (me.id) {
      setPagesList(me?.isAdmin ? adminPagesList : initialPagesList);
    }
  }, [me]);

  const handleNavigateToPage = (url: string) => {
    navigate(url);
  };

  const handleToggleSidebar = () => {
    setIsExpanded((prevState) => {
      const newState = !prevState;
      return newState;
    });
  };

  return (
    <AppBox
      fullHeight
      className="w-[60px] hover:w-[220px] transition-all bg-white shadow-md pt-[68px] fixed z-50 overflow-hidden hidden md:flex"
      onMouseEnter={handleToggleSidebar}
      onMouseLeave={handleToggleSidebar}
    >
      <AppBox fullWidth column justify="between" fullHeight>
        {/* Pages List */}
        <AppBox column fullWidth>
          {me.id &&
            pagesList.map((page, index) => {
              if (
                (page.name === "Agency" && !me.is_agency_admin) ||
                (page.name === "Billing" && me.agency?.id && !me.is_agency_admin) ||
                (page.name === "Integrations" && (me.integrations && !me.integrations.length))
              ) {
                return null;
              }

              const showPage = page.protected
                ? me.isSubscribed && !me.isCanceled
                : !page.protected;
                
              const isSelected = location.pathname === page.url;
              
              return (
                showPage && (
                  <AppBox column key={index} fullWidth align="center">
                    <AppBox align="center" justify="between" gap={2} fullWidth
                      onClick={() =>
                        page.url
                          ? handleNavigateToPage(page.url)
                          : setMenuOpen(!menuOpen)
                      }
                      className={clsx(
                        "px-5 py-4 cursor-pointer",
                        isSelected && !me.isAdmin || (isSelected && page.url !== '/panel')
                          ? "bg-gray-100 text-gray-800"
                          : "text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      <AppBox align="center" gap={2}>
                        <AppIcon name={page.icon} />
                        <AppText className={clsx("whitespace-nowrap opacity-0", {"opacity-100": isExpanded})}>{page.name}</AppText>
                      </AppBox>

                      {
                        page.children.length
                          ? <AppIcon
                              name="chevronDown"
                              className={clsx({"rotate-180": menuOpen})}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                setMenuOpen(!menuOpen)
                              }}
                            />
                          : null
                      }
                    </AppBox>

                    {page.children.length > 0 && menuOpen && (
                      <AppBox column fullWidth>
                        {page.children.map((child) => {
                          // Update child name if it's "Overall Reports" and user is agency admin
                          const displayName = child.name === "Overall Reports" && me.is_agency_admin 
                            ? "Agency Reports"
                            : child.name;

                          return (
                            <AppBox
                            key={child.url}
                            onClick={() => handleNavigateToPage(child.url)}
                            gap={1}
                            align="center"
                            className={clsx(
                              "cursor-pointer py-4 px-5",
                              isExpanded ? "pl-8" : "pl-5",
                              location.pathname === child.url
                                ? "bg-gray-100"
                                : "hover:bg-gray-50"
                            )}
                          >
                            <AppIcon name={child.icon} className="flex-shrink-0" />
                            <AppText className={clsx("whitespace-nowrap opacity-0", {"opacity-100": isExpanded})}>{displayName}</AppText>
                          </AppBox>
                        );
                      })}
                      </AppBox>
                    )}
                  </AppBox>
                )
              );
            })}
        </AppBox>

        {/* Toggle Sidebar Button */}
        {/* <AppBox justify="center">
          <AppBox onClick={handleToggleSidebar} align="center" justify="center" className="p-2 cursor-pointer rounded bg-gray-100 hover:bg-gray-200">
            <AppIcon
              name="chevronRight"
              className="text-gray-600"
              width={24}
              height={24}
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            />
          </AppBox>
        </AppBox> */}
      </AppBox>
    </AppBox>
  );
};

export default AppSidebar;